import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import { Control, Path, useWatch } from "react-hook-form";

import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";

import { isValid } from "date-fns";

import CheckboxFormField from "components/checkbox-form-field";
import SelectFormField from "components/select-form-field";
import {
  RECURRENCE_DAYS,
  RECURRENCE_WEEKS,
  REOCCURRENCE_PERIODS,
  REOCCURRENCE_PERIOD_TYPE_DAILY,
  REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH,
  REOCCURRENCE_PERIOD_TYPE_MONTHLY,
  REOCCURRENCE_PERIOD_TYPE_WEEKLY
} from "constants/appointments";
import { NTH_WEEKDAY_OF_MONTH_SCHEDULING_FEATURE } from "constants/feature-flags";

import { AppointmentFormValues } from "../appointment-form.component";

type RecurringPeriodFieldProps = {
  control: Control<AppointmentFormValues, object>;
  contentSx?: SxProps;
  recurrenceReadonly?: boolean;
  exclude?: string[];
  setValue: (
    name: Path<AppointmentFormValues>,
    value: any,
    options?: any
  ) => void;
};

export default function RecurringPeriodField({
  control,
  setValue,
  contentSx = {},
  recurrenceReadonly = false,
  exclude = []
}: RecurringPeriodFieldProps) {
  const flags = useFlags();
  const isNthWeekDayEnabled = flags[NTH_WEEKDAY_OF_MONTH_SCHEDULING_FEATURE];
  const [dayChanged, setDayChanged] = useState(false);

  const [isRecurring, recurrencePeriod, appointmentDate] = useWatch({
    control,
    name: ["is_recurring", "recurrence_period", "appointment_date"]
  });

  useEffect(() => {
    if (!dayChanged && recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_WEEKLY) {
      const date = isValid(appointmentDate) ? appointmentDate : new Date();
      let day = date.getDay();

      if (day === 0) day = 6;

      setValue("recurrence_day", day);
      setDayChanged(true);
    }
  }, [dayChanged, recurrencePeriod, setValue, appointmentDate]);

  useEffect(() => {
    if (recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_MONTHLY) {
      const date = isValid(appointmentDate) ? appointmentDate : new Date();
      setValue("recurrence_day", date.getDate());
      setDayChanged(true);
    } else if (recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_DAILY) {
      setValue("recurrence_day", "");
      setValue("recurrence_week", "");
      setDayChanged(true);
    }
  }, [recurrencePeriod, setValue, appointmentDate]);

  const options = useMemo(() => {
    return REOCCURRENCE_PERIODS.getOptions(
      isNthWeekDayEnabled
        ? [...exclude]
        : [...exclude, REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH]
    );
  }, [exclude, isNthWeekDayEnabled]);

  return (
    <Box display="flex" justifyContent="flex-start" alignItems="flex-start">
      <EventRepeatIcon sx={{ mt: 1.5, mr: 1.5 }} />

      <Stack
        direction="row"
        justifyContent="flex-start"
        sx={{ minWidth: "131px", mr: 0.35 }}
      >
        <FormControlLabel
          label="Is recurring"
          labelPlacement="start"
          sx={{ ml: 0 }}
          disabled={recurrenceReadonly}
          control={
            <CheckboxFormField
              testId="is_recurring"
              name="is_recurring"
              control={control}
            />
          }
        />
      </Stack>

      {isRecurring && (
        <Stack direction="column" spacing={2.5} width="100%" sx={contentSx}>
          <SelectFormField
            name="recurrence_period"
            label=""
            required
            control={control}
            options={options}
          />
          {recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_WEEKLY && (
            <SelectFormField
              name="recurrence_day"
              label="Recurrence Day"
              required
              control={control}
              options={RECURRENCE_DAYS.getOptions()}
            />
          )}
          {recurrencePeriod ===
            REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH && (
            <>
              <SelectFormField
                name="recurrence_day"
                label="Recurrence Day"
                required
                control={control}
                options={RECURRENCE_DAYS.getOptions()}
              />
              <SelectFormField
                name="recurrence_week"
                label="Recurrence Week"
                required
                control={control}
                options={RECURRENCE_WEEKS.getOptions()}
              />
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}
